import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/lib/api';

/**
 * Turn anything thrown by the API client into a human message (05 §6.3).
 *
 * The lookup is `errors.<CODE>` in the active locale; an unknown code falls back
 * to `errors.UNKNOWN` rather than leaking the server's English `message`, because
 * a TA reading an Arabic screen should never see a raw enum.
 */
export function useErrorMessage(): (error: unknown) => string {
  const { t } = useTranslation();

  return useCallback(
    (error: unknown): string => {
      if (error instanceof ApiError) {
        const key = `errors.${error.code}`;
        const translated = t(key);
        if (translated !== key) return translated;
        return t('errors.UNKNOWN');
      }
      if (error instanceof Error && error.message) return error.message;
      return t('errors.UNKNOWN');
    },
    [t],
  );
}
